import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Avatar, Button, Input } from '@roger/ui-components';
import { ArrowLeft } from 'lucide-react';
import { useStore } from '../../store';
import {
  chatKeys,
  useAnnouncerRoleId,
  useChannel,
} from '../../hooks/chat/useChatQueries';
import {
  createGroupAssignment,
  deleteAssignment,
  getUsersByPermission,
  listGroupAssignments,
} from '../../services/api';
import type { GroupAssignmentResponse } from '../../services/api';
import { getBusinessErrorMessage } from '../../utils/apiErrorDetail';
import { useGroupDetail } from '../group-manager/useGroupAdminQueries';
import { initialsOf } from './initials';

/** True when every typed word starts a word of the name. */
function matchesName(name: string, words: string[]): boolean {
  const parts = name.toLowerCase().split(/\s+/);
  return words.every(word => parts.some(part => part.startsWith(word)));
}

export function AnnouncersView({
  channelId,
  onBack,
  touch = false,
}: {
  channelId: string;
  onBack: () => void;
  /** Phone sheet: a 44 px Back. */
  touch?: boolean;
}) {
  const staffAdmitted = useStore(
    state => state.user?.agency?.system_name === 'Strawhat'
  );
  const showError = useStore(state => state.showError);
  const queryClient = useQueryClient();
  const { data: header } = useChannel(channelId);
  const groupId = header?.group_id ?? '';
  const { roleId, isError: roleError } = useAnnouncerRoleId(!!groupId);

  const [query, setQuery] = useState('');
  // Only live members are offered (ruling R-02): a non-member
  // announcer could post but never read the channel. A Roger employee is
  // offered as a candidate only at Strawhat.
  const { data: group, isError: groupError } = useGroupDetail(groupId || null);

  const { data: assignments, isError: assignmentsError } = useQuery({
    queryKey: chatKeys.announcers(groupId),
    queryFn: async () => {
      const { data } = await listGroupAssignments({
        path: { group_id: groupId },
        throwOnError: true,
      });
      return data ?? [];
    },
    enabled: !!groupId,
  });

  // Agency-wide holders post in every announcement channel.
  const { data: holders, isError: holdersError } = useQuery({
    // A sibling key, not under chatKeys.announcers(groupId): Add and Remove
    // invalidate that prefix and must not refetch this agency-wide list.
    queryKey: ['chat', 'announcement-holders'],
    queryFn: async () => {
      const { data } = await getUsersByPermission({
        query: { permission: 'chat:send-announcement' },
        throwOnError: true,
      });
      return data ?? [];
    },
    enabled: !!groupId,
  });

  // The agency-wide list keeps Roger employees only at Strawhat.
  const admins = (holders ?? []).filter(
    user => staffAdmitted || !user.is_roger_employee
  );
  // One row per person: an admin's group grant would add a second row whose
  // Remove changes nothing. Built from every holder, before the Roger-employee
  // drop: a Roger employee with both grants keeps posting after Remove.
  const adminIds = new Set((holders ?? []).map(user => user.id));
  // Without the holders every grant looks removable, and Remove there takes a
  // group grant from someone who also holds the agency-wide one. Show none.
  const removable = holdersError
    ? []
    : (assignments ?? []).filter(
        assignment => !adminIds.has(assignment.user_id)
      );
  // Someone who already announces is not offered: Add would change nothing.
  const announcing = new Set([
    ...adminIds,
    ...(assignments ?? []).map(assignment => assignment.user_id),
  ]);
  const words = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
  // Without the holders `announcing` omits the agency-wide grants, so Add is
  // offered to someone who already announces. That grant then sits in the
  // admin list, which carries no Remove. Offer nobody.
  const matches =
    words.length === 0 || holdersError
      ? []
      : (group?.members ?? []).filter(
          member =>
            (staffAdmitted || !member.is_roger_employee) &&
            !announcing.has(member.user_id) &&
            matchesName(member.name, words)
        );

  // The list response names every announcer, member or not.
  const nameOf = (assignment: GroupAssignmentResponse) =>
    assignment.user_name || assignment.user_id;

  const refresh = () =>
    queryClient.invalidateQueries({ queryKey: chatKeys.announcers(groupId) });

  const add = async (userId: string) => {
    if (!roleId) return;
    try {
      await createGroupAssignment({
        body: { user_id: userId, role_id: roleId, group_id: groupId },
        throwOnError: true,
      });
      setQuery('');
      await refresh();
    } catch (error) {
      console.error('Add announcer failed', error);
      showError(
        'Failed to add announcer',
        getBusinessErrorMessage(error) ?? undefined
      );
    }
  };

  const remove = async (assignmentId: string) => {
    try {
      await deleteAssignment({
        path: { assignment_id: assignmentId },
        throwOnError: true,
      });
      await refresh();
    } catch (error) {
      console.error('Remove announcer failed', error);
      showError(
        'Failed to remove announcer',
        getBusinessErrorMessage(error) ?? undefined
      );
    }
  };

  return (
    <>
      <div className='flex items-center gap-1 border-b px-2 py-2'>
        {/* autoFocus: the control that opened this view is gone, so keys need a home. */}
        <Button
          size={touch ? 'iconLg' : 'icon'}
          variant='ghost'
          aria-label='Back'
          title='Back'
          autoFocus
          onClick={onBack}
        >
          <ArrowLeft className='h-3.5 w-3.5' />
        </Button>
        <p className='flex-1 text-sm font-semibold'>Announcers</p>
      </div>
      <p className='px-3 py-2 text-xs text-muted-foreground'>
        Admins can always post here. Add people who may also post.
      </p>

      <div className='px-3'>
        <Input
          aria-label='Search people'
          inputSize='sm'
          value={query}
          onChange={e => setQuery(e.target.value)}
          placeholder='Search people'
        />
      </div>

      {/* An empty list after a failed read would repeat the A1-04 defect. */}
      {(assignmentsError || holdersError || groupError || roleError) && (
        <p className='px-3 py-2 text-xs text-muted-foreground'>
          Unable to load people. Please try again.
        </p>
      )}

      <div className='flex-1 overflow-y-auto'>
        {matches.map(member => (
          <div
            key={member.user_id}
            className='flex items-center gap-2 px-3 py-2 text-sm'
          >
            <Avatar size='sm' initials={initialsOf(member.name)} />
            <span className='flex-1'>{member.name}</span>
            <Button
              size='sm'
              aria-label={`Add ${member.name}`}
              disabled={!roleId}
              onClick={() => void add(member.user_id)}
            >
              Add
            </Button>
          </div>
        ))}

        {admins.map(user => {
          const name = `${user.first_name} ${user.last_name}`.trim();
          return (
            <div
              key={user.id}
              className='flex items-center gap-2 px-3 py-2 text-sm'
            >
              <Avatar size='sm' initials={initialsOf(name)} />
              <span className='flex-1'>{name}</span>
            </div>
          );
        })}

        {removable.map(assignment => (
          <div
            key={assignment.id}
            className='flex items-center gap-2 px-3 py-2 text-sm'
          >
            <Avatar size='sm' initials={initialsOf(nameOf(assignment))} />
            <span className='flex-1'>{nameOf(assignment)}</span>
            <Button
              size='sm'
              variant='ghost'
              aria-label={`Remove ${nameOf(assignment)}`}
              onClick={() => void remove(assignment.id)}
            >
              Remove
            </Button>
          </div>
        ))}
      </div>
    </>
  );
}
